// THE HOMEPAGE. Landing page for LedgerAI — the crypto reconciliation
// service, powered by the deterministic engine and the interactive /try flow.
// (Fiat surface deferred; hero + meta updated 2026-08-23 to crypto-only.
// COMPARISONS[] below is dead code — this route renders only Hero / Protocols
// / WhatYouGet — but it still carries a stale "crypto + fiat" row; leave for a
// dead-code sweep, not part of this edit.)
//
// Consolidated 2026-08-17 from the previous split of `/` (service-only pitch)
// + `/preview` (Icon-shape software pitch). `/preview` retired in the same
// change; sections migrated here in a single flow. Where the two pages
// overlapped, the /preview version won (hero with TryCarousel, timed
// How-it-works cards, Calendly + Try free dual CTAs).
//
// LOAD-BEARING CONSTRAINTS (do not weaken without explicit call):
//   1. SCOPE BOUNDARY — the engagement stops at the pack; the client's own
//      accountant or registered tax agent files it. Never imply lodgement,
//      return preparation, or tax advice. This is what keeps it outside
//      Australia's TASA 2009 — and outside the equivalent licensing regime in
//      every other jurisdiction, which is why the boundary must hold in the
//      GENERIC copy, not just the AU copy.
//   2. Never market "recovery" for lost/stolen crypto — that is the scam-
//      recovery script. We sell the documented number, not money back.
//   3. JURISDICTION-NEUTRAL BASE COPY: all country-specific claims are
//      confined to the Jurisdictions section so they can be added, trimmed or
//      corrected in one place. Do not sprinkle them back through the page.
//   4. Dollar figures never for user-pasted addresses (only for seeded demo
//      fixtures) — the /wallet-check retirement exit criterion. /try honours
//      this today.
//
// 2026-08-21 REFRAME — from service-first to per-protocol PnL:
//   - The page now sells the PRODUCT (automated per-protocol PnL, coverage
//     inventory rendered as the Decoded-protocols section). Service is the
//     fallback for the residual margin of error and is upsold on the P&L
//     surface itself (/try output + /report/pnl), NOT here on the homepage.
//     Do NOT re-add a service pitch as the hero frame. Reason: service-
//     first pricing is anchored to a gap the decoders keep closing, so it
//     drifts out of honesty as coverage grows. See ideas-store/ledgerai.md
//     wedge update + the roadmap doc (issue 840, when filed).
//   - p2 hero copy is the reframe pivot: "Start automated ... system works
//     out what it can and adds a margin of error - we work together to
//     close it." Do not restore the "I reconcile by hand" line.
//
// RETIRED constraints from the pre-consolidation `/` header:
//   - "Self-contained ON PURPOSE… sells a SERVICE, not the software." The
//     page now sells the product; the /try flow is the interactive proof.
//   - "Do NOT re-add a 'Check a wallet — free' CTA without the route." /try
//     IS the real interactive flow (not the synthetic-report antipattern the
//     original constraint was written against). Both the Try-free CTA in the
//     hero and the interactive carousel below are honest.

import {
	BarChart3,
	Banknote,
	Coins,
	FileImage,
	FileSpreadsheet,
	FileText,
	Rows3,
	Scale,
	Share2,
	Video,
	type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { AuroraText } from '@/components/magicui/aurora-text'
import { BlurFade } from '@/components/magicui/blur-fade'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import Carousel, {
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from '#app/components/ui/carousel.tsx'
import { SiteSection } from '#app/components/site-section.tsx'
import { type Route } from './+types/index.ts'

// Hero gallery — snapshots captured from /capture/hero (admin-only,
// dev-only). Add entries to grow the carousel.
//
// **ALWAYS capture BOTH light + dark themes when regenerating any
// hero image.** `scripts/capture-hero.mjs` does both by default; the
// carousel below picks the variant that matches the viewer's theme
// via useTheme (cookie-driven). A light-only or dark-only hero flips
// jarringly on theme toggle. See the script header for the full rule.
const HERO_IMAGES: Array<{ srcLight: string; srcDark: string; alt: string }> = [
	{
		srcLight: '/img/hero-wallet-summary-light.png',
		srcDark: '/img/hero-wallet-summary-dark.png',
		alt: 'LedgerAI wallet summary — realised P&L, HODL baseline, and position-level breakdown for a Meteora LP wallet.',
	},
]

const PRIMARY_KEYWORD = 'crypto tax reconciliation'

// Depth is honest, not aspirational — `deep` means the engine models that
// jurisdiction's rules directly; `pack` means jurisdiction-neutral workpapers
// the client's local accountant applies their own rules to. Do not promote a
// row to `deep` without the engine actually implementing it.
const JURISDICTIONS = [
	{
		flag: '🇦🇺',
		country: 'Australia',
		depth: 'deep' as const,
		p: 'CGT with the 12-month discount applied per parcel, Australian financial years, ATO evidentiary expectations on cost base. Self-lodgers are due 31 October; through a registered agent you generally have longer.',
	},
	{
		flag: '🇺🇸',
		country: 'United States',
		depth: 'pack' as const,
		p: 'Short- and long-term disposals separated by holding period on a calendar year, ready for your CPA to take to Form 8949 and Schedule D.',
	},
	{
		flag: '🇬🇧',
		country: 'United Kingdom',
		depth: 'pack' as const,
		p: 'Disposals and acquisitions dated and evidenced for your accountant to run HMRC’s pooling and 30-day rules over.',
	},
	{
		flag: '🇨🇦',
		country: 'Canada',
		depth: 'pack' as const,
		p: 'Dispositions with proceeds and cost, ready for adjusted-cost-base treatment and the capital-versus-business-income call your accountant makes.',
	},
	{
		flag: '🇳🇿',
		country: 'New Zealand',
		depth: 'pack' as const,
		p: 'Full acquisition and disposal history with purpose evidence, for your accountant to assess against the intention test.',
	},
	{
		flag: '🌍',
		country: 'Anywhere else',
		depth: 'pack' as const,
		p: 'The reconstruction does not change. You get every disposal, its cost base and its evidence in a neutral format your local accountant applies their own rules to.',
	},
]

// Decoded-protocol inventory. Status is the honest coverage claim:
//   'live'    -> decoder ships today; chip renders in full colour.
//   'wip'     -> actively being decoded now; chip renders greyscale.
//   'roadmap' -> recognised, not decoded yet; chip renders as an outline
//                (b&w). Anything on the roadmap becomes margin-of-error on
//                the report, per the hero's p2 promise.
// Ground truth for 'live': per-protocol PnL is decoded end-to-end and has
// been verified on a real book. DEX_SOURCES (in
// app/features/chain/sync-v2.server.ts) tags a tx as coming FROM a program,
// which is a strictly weaker claim; having an entry there is a necessary
// but insufficient condition. Same for importer files under
// app/features/chain/importers/ - a file existing does not imply the
// per-exchange PnL is production-hardened. As of 2026-08-21 the only
// live entries are Solana (chain-level ingest), Meteora (protocol PnL),
// Xero (OAuth + reconciliation), Binance (API), KuCoin (API) and FTX (CSV).
// Exchange chips carry their integration mode in the category string so a
// reader can see what the live claim is grounded in.
// Do NOT promote a chip to 'live' without one of the above. Do NOT demote
// to 'roadmap' if the code is there - a chip lying either way makes p2's
// margin-of-error promise dishonest.
type ProtocolStatus = 'live' | 'wip' | 'roadmap'
type Protocol = {
	label: string
	category: string
	tint: string
	status: ProtocolStatus
	simpleIcon?: string
}

const PROTOCOLS: Array<Protocol> = [
	// Live (colour) - per-protocol PnL decoded end-to-end today. Exchanges
	// carry their integration mode in the category (api vs csv) so a reader
	// can tell what a live claim is grounded in.
	{ label: 'Solana', category: 'Chain', tint: 'bg-purple-500', status: 'live', simpleIcon: 'solana' },
	{ label: 'Meteora', category: 'Solana DEX', tint: 'bg-pink-500', status: 'live', simpleIcon: 'meteora' },
	/*
	Commented out pre-launch — chips shown must be implemented end-to-end.
	Restore individual entries as each protocol ships and is verified live.
	Preserving the entries (not deleting) so restoration is a diff-review,
	not a re-author.

	{ label: 'Xero', category: 'Accounting', tint: 'bg-sky-500', status: 'wip', simpleIcon: 'xero' },
	{ label: 'Binance', category: 'Exchange · API', tint: 'bg-yellow-600', status: 'wip', simpleIcon: 'binance' },
	{ label: 'KuCoin', category: 'Exchange · API', tint: 'bg-teal-500', status: 'wip', simpleIcon: 'kucoin' },
	{ label: 'FTX', category: 'Exchange · CSV', tint: 'bg-slate-500', status: 'wip', simpleIcon: 'ftx' },
	{ label: 'MYOB', category: 'Accounting', tint: 'bg-violet-600', status: 'wip', simpleIcon: 'myob' },
	{ label: 'Mews', category: 'Hospitality PMS', tint: 'bg-cyan-500', status: 'wip' },
	{ label: 'Jupiter', category: 'Solana DEX', tint: 'bg-emerald-500', status: 'roadmap' },
	{ label: 'Pumpfun', category: 'Solana DEX', tint: 'bg-green-500', status: 'roadmap' },
	{ label: 'Raydium', category: 'Solana DEX', tint: 'bg-sky-500', status: 'roadmap' },
	{ label: 'Orca', category: 'Solana DEX', tint: 'bg-cyan-500', status: 'roadmap' },
	{ label: 'Phoenix', category: 'Solana DEX', tint: 'bg-orange-500', status: 'roadmap' },
	{ label: 'Lifinity', category: 'Solana DEX', tint: 'bg-teal-500', status: 'roadmap' },
	{ label: 'OpenBook', category: 'Solana DEX', tint: 'bg-blue-500', status: 'roadmap' },
	{ label: 'Saber', category: 'Solana DEX', tint: 'bg-indigo-500', status: 'roadmap' },
	{ label: 'Serum', category: 'Solana DEX', tint: 'bg-violet-500', status: 'roadmap' },
	{ label: 'Drift', category: 'Solana perps', tint: 'bg-purple-500', status: 'roadmap' },
	{ label: 'Marinade', category: 'Solana LST', tint: 'bg-lime-500', status: 'roadmap' },
	{ label: 'Kamino', category: 'Solana lending', tint: 'bg-blue-500', status: 'roadmap' },
	{ label: 'Solend', category: 'Solana lending', tint: 'bg-amber-500', status: 'roadmap' },
	{ label: 'Magic Eden', category: 'Solana NFT', tint: 'bg-fuchsia-500', status: 'roadmap' },
	{ label: 'Ethereum', category: 'Chain', tint: 'bg-indigo-500', status: 'roadmap', simpleIcon: 'ethereum' },
	{ label: 'Bitcoin', category: 'Chain', tint: 'bg-amber-500', status: 'roadmap', simpleIcon: 'bitcoin' },
	{ label: 'Coinbase', category: 'Exchange', tint: 'bg-blue-600', status: 'roadmap', simpleIcon: 'coinbase' },
	{ label: 'Bybit', category: 'Exchange', tint: 'bg-orange-500', status: 'roadmap' },
	{ label: 'Swyftx', category: 'Exchange · AU', tint: 'bg-lime-600', status: 'roadmap' },
	{ label: 'CoinSpot', category: 'Exchange · AU', tint: 'bg-blue-500', status: 'roadmap' },
	{ label: 'CoinJar', category: 'Exchange · AU', tint: 'bg-red-500', status: 'roadmap' },
	{ label: 'Celsius', category: 'Exchange', tint: 'bg-slate-500', status: 'roadmap' },
	{ label: 'QuickBooks', category: 'Accounting', tint: 'bg-emerald-600', status: 'roadmap', simpleIcon: 'quickbooks' },
	*/
]

// File formats are inputs, not protocols; they don't get decoded, they get
// read. Kept as a small footnote so the "send everything" spirit survives
// without diluting the protocol coverage claim.
const ACCEPTED_FORMATS: Array<{ label: string; icon: LucideIcon }> = [
	{ label: 'CSV', icon: FileSpreadsheet },
	{ label: 'Excel', icon: FileSpreadsheet },
	{ label: 'PDF', icon: FileText },
	{ label: 'Screenshots', icon: FileImage },
	{ label: 'Screen recordings', icon: Video },
]

const STEPS: Array<{ when: string; title: string; body: string }> = [
	{
		when: 'Day 0',
		title: 'Schedule a call, or send a message',
		body: 'What you hold, which years are open, where the records stop. You get a price scaled to volume and complexity — or an honest "I can\'t help with this".',
	},
	{
		when: 'Day 1–2',
		title: 'You send what you have',
		body: 'Exchange exports, public wallet addresses (read-only), bank statements. Whatever exists — the gaps are the job.',
	},
	{
		when: 'Day 3–10',
		title: 'I reconcile',
		body: 'The automated pipeline handles volume; I handle the tail by hand. Where a judgement is required, it is recorded with its reasoning.',
	},
	{
		when: 'Day 10–14',
		title: 'You get the pack',
		body: 'A reviewed capital-gains pack, ready for your accountant. Questions answered until they are satisfied.',
	},
]

type Cell = 'yes' | 'no' | 'partial' | string
const COMPARISONS: Array<{
	feature: string
	ledgerai: Cell
	koinly: Cell
	ctc: Cell
	xero: Cell
	myob: Cell
}> = [
	{ feature: 'Crypto CGT with FIFO / spec-id', ledgerai: 'yes', koinly: 'yes', ctc: 'yes', xero: 'no', myob: 'no' },
	{ feature: 'Fiat double-entry bookkeeping', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'yes', myob: 'yes' },
	{ feature: 'One book for crypto + fiat', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'no', myob: 'no' },
	{ feature: 'Dead exchange reconstruction', ledgerai: 'yes', koinly: 'partial', ctc: 'partial', xero: 'no', myob: 'no' },
	{ feature: 'DeFi + LP + cross-chain', ledgerai: 'yes', koinly: 'partial', ctc: 'partial', xero: 'no', myob: 'no' },
	{ feature: 'Missing-cost-base reconstruction', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'no', myob: 'no' },
	{ feature: 'Bank feed reconciliation', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'yes', myob: 'yes' },
	{ feature: 'Intercompany between entities', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'partial', myob: 'partial' },
	{ feature: "Records stay on your operator's machine", ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'no', myob: 'no' },
	{ feature: 'Delivered as pack for YOUR accountant to file', ledgerai: 'yes', koinly: 'no', ctc: 'no', xero: 'no', myob: 'no' },
]

// Widened 2026-08-12 to match the crypto+fiat hero. CRYPTO LEADS EVERY STRING
// deliberately — it is the wedge, it is what someone actually types into a
// search box, and there is no query volume for "fiat reconciliation". Fiat is
// the second clause, never the first. If a future edit reverses that order it
// will read as broader and rank for less.
export const meta: Route.MetaFunction = () => [
	{ title: 'The statements other software gave up on | LedgerAI' },
	{
		name: 'description',
		content:
			'The statements other software gave up on — General Ledger, P&L, Balance Sheet, Chart of Accounts, Unpriced lots — for crypto and fiat, in one book.',
	},
	{ name: 'keywords', content: PRIMARY_KEYWORD },
	{
		property: 'og:title',
		content: 'The statements other software gave up on',
	},
	{
		property: 'og:description',
		content:
			'The messy tail other software leaves to you — dead exchanges, missing cost base, DeFi and bridges — reconciled by hand into a pack your own accountant files from.',
	},
	{ property: 'og:type', content: 'website' },
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:site', content: '@underemployed' },
	{ name: 'twitter:creator', content: '@underemployed' },
	{
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'Service',
			name: 'Crypto reconciliation (done for you)',
			serviceType: 'Cryptocurrency reconciliation',
			areaServed: JURISDICTIONS.filter((j) => j.country !== 'Anywhere else').map(
				(j) => ({ '@type': 'Country', name: j.country }),
			),
			description:
				'Hand-reconciled crypto workpapers, delivered as a pack for the client’s own accountant or registered tax agent to file from.',
		},
	},
]

// ---------------------------------------------------------------------------
// Section shell — matches /try's conventions.
//   - narrow (default) → max-w-3xl for text-first sections
//   - wide (`wide`)    → max-w-7xl for grid/card sections
//   - vertical rhythm  → py-6 md:py-8
// ---------------------------------------------------------------------------


// Homepage is static — no loader, no DB reads. The hero's wallet card
// is a PNG snapshot captured by the operator at /capture/hero (admin-
// only). Live card lives behind auth; see issue 1032 for the retire-
// anon-reads plan.

export default function HomeRoute() {
	return (
		<main className="flex w-full flex-col items-center">
			<PositionHero />
			<Protocols />
			<Features />
			<Pricing />
			<FAQ />
			<Contact />
		</main>
	)
}

// ---------------------------------------------------------------------------
// Features — capabilities the engine exposes, framed for a visitor
// deciding whether this software covers their workflow. Not routes;
// capabilities. Authored in status order (live → wip) so the section
// reads as a growing surface rather than a mixed grid. Add new rows
// in the right status block.
// ---------------------------------------------------------------------------

type Feature = {
	label: string
	icon: LucideIcon
	status: 'live' | 'wip'
	blurb: string
}

const FEATURES: Feature[] = [
	{
		label: 'P&L',
		icon: BarChart3,
		status: 'live',
		blurb:
			'Lot-matched realised P&L and LP-cycle P&L, with a vs-HODL baseline so the number is honest about opportunity cost.',
	},
	{
		label: 'Blotter',
		icon: Rows3,
		status: 'live',
		blurb:
			'One row per economic event — deposits, withdrawals, fee claims, swaps — sortable, filterable, FY-scopable.',
	},
	{
		label: 'Crypto reconciliation',
		icon: Coins,
		status: 'live',
		blurb:
			'Per-protocol observers (Meteora today; Raydium, Jupiter, Kamino next) decode on-chain txs into typed events, not opaque transfers.',
	},
	{
		label: 'Fiat reporting',
		icon: Banknote,
		status: 'live',
		blurb:
			'Reporting currency per book — AUD, USD, EUR. Daily FX anchors every valuation to a single date, not a mix of live spot and stale rates.',
	},
	{
		label: 'Shareable cards',
		icon: Share2,
		status: 'live',
		blurb:
			'Per-position and per-wallet artefacts you can post publicly — image or short GIF — without exposing the underlying book.',
	},
	{
		label: 'Balance sheet',
		icon: Scale,
		status: 'wip',
		blurb:
			'Double-entry account model is live; the balance-sheet surface renders on top of the same cube that already drives P&L.',
	},
]

function Features() {
	return (
		<SiteSection wide id="features">
			<BlurFade inView>
				<p className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
					Features
				</p>
				<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
					What you can do with it.
				</h2>
				<p className="text-muted-foreground mt-3 max-w-2xl text-lg">
					Capabilities, not routes. Everything below works on the same
					book your reports read from.
				</p>
			</BlurFade>
			<BlurFade delay={0.1} inView>
				<div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{FEATURES.map((f) => (
						<FeatureCard key={f.label} feature={f} />
					))}
				</div>
			</BlurFade>
		</SiteSection>
	)
}

function FeatureCard({ feature }: { feature: Feature }) {
	const Icon = feature.icon
	const isLive = feature.status === 'live'
	return (
		<div
			className={cn(
				'flex flex-col gap-3 rounded-lg border p-4',
				isLive
					? 'border-border/60 bg-card/40'
					: 'border-border/40 bg-card/20 opacity-80',
			)}
		>
			<div className="flex items-center justify-between">
				<Icon className="text-muted-foreground h-5 w-5" />
				{!isLive ? (
					<span className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
						Coming
					</span>
				) : null}
			</div>
			<div>
				<h3 className="font-semibold">{feature.label}</h3>
				<p className="text-muted-foreground mt-1 text-sm">{feature.blurb}</p>
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// 0. Wallet Hero — copy left, PNG snapshot right. The live
//    WalletSummaryCard is authed-only (issue 1032); the homepage shows
//    a captured artefact. Re-capture via /capture/hero when the
//    underlying book changes meaningfully.
// ---------------------------------------------------------------------------

function PositionHero() {
	return (
		<SiteSection wide>
			<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)] lg:gap-10">
				<div className="max-w-xl">
					<p className="text-muted-foreground mb-4 text-sm font-medium tracking-wide uppercase">
						Crypto & DeFi taxes
					</p>
					<h1 className="text-4xl font-bold tracking-tight text-balance md:text-5xl">
						The statements other software{' '}
						<AuroraText>gave up on</AuroraText>.
					</h1>
					<p className="text-muted-foreground mt-6 text-base md:text-lg">
						Koinly, Summ (CTC) — handle the easy 90% of your crypto.
						LedgerAI handles the hard 10%. Your DeFi transactions.
					</p>
					<div className="mt-6 flex flex-col gap-3 sm:flex-row">
						<a
							href="https://x.com/underemployed"
							target="_blank"
							rel="noopener noreferrer"
							className={cn(buttonVariants({ size: 'lg' }))}
						>
							Request invitation →
						</a>
						<a
							href="https://x.com/underemployed"
							target="_blank"
							rel="noopener noreferrer"
							className={cn(buttonVariants({ size: 'lg', variant: 'outline' }))}
						>
							See examples on X →
						</a>
					</div>
					<p className="text-muted-foreground mt-4 text-xs">
						Reading wallets takes a lot of API calls — sign up to search
						live wallets (it's free). Browse the X account above for
						report examples.
					</p>
				</div>

				<div>
					<Carousel opts={{ loop: HERO_IMAGES.length > 1 }}>
						<CarouselContent>
							{HERO_IMAGES.map((img) => (
								<CarouselItem key={img.srcLight}>
									{/* Render both variants; CSS gates visibility
									    on the <html>.dark class set by the theme
									    provider. Avoids a hydration flash the
									    client-side useTheme route would introduce
									    on first paint. */}
									<img
										src={img.srcLight}
										alt={img.alt}
										className="w-full rounded-lg border shadow-sm dark:hidden"
									/>
									<img
										src={img.srcDark}
										alt={img.alt}
										className="hidden w-full rounded-lg border shadow-sm dark:block"
									/>
								</CarouselItem>
							))}
						</CarouselContent>
						{HERO_IMAGES.length > 1 ? (
							<>
								<CarouselPrevious />
								<CarouselNext />
							</>
						) : null}
					</Carousel>
				</div>
			</div>
		</SiteSection>
	)
}


// ---------------------------------------------------------------------------
// 2. Protocols (coverage inventory)
// ---------------------------------------------------------------------------

function Protocols() {
	// PROTOCOLS is authored in status order (live -> wip -> roadmap); relying
	// on authoring order keeps the render pure and diff-reviewable. Adding a
	// protocol out of group means placing it in the correct block up top.
	return (
		<SiteSection wide id="protocols">
			<BlurFade inView>
				<p className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
					Coverage
				</p>
				<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
					Supported chains and protocols.
				</h2>
				<p className="text-muted-foreground mt-3 max-w-2xl text-lg">
					A growing list.
				</p>
			</BlurFade>
			<BlurFade delay={0.1} inView>
				<div className="mt-6 flex flex-wrap gap-2">
					{PROTOCOLS.map((p) => (
						<ProtocolChip key={p.label} protocol={p} />
					))}
				</div>
			</BlurFade>
			<BlurFade delay={0.2} inView>
				<div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
					<span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
						Also accepted
					</span>
					{ACCEPTED_FORMATS.map(({ label, icon: Icon }) => (
						<span
							key={label}
							className="text-muted-foreground inline-flex items-center gap-1.5 text-sm"
						>
							<Icon className="size-3.5" />
							{label}
						</span>
					))}
				</div>
			</BlurFade>
		</SiteSection>
	)
}

function ProtocolChip({ protocol }: { protocol: Protocol }) {
	const initials = protocol.label
		.split(/\s/)
		.map((w) => w[0])
		.slice(0, 2)
		.join('')
		.toUpperCase()

	// Three status-driven variants. Structure is identical; only tint,
	// grayscale filter, opacity, and border/text muting change.
	const isLive = protocol.status === 'live'
	const isWip = protocol.status === 'wip'
	const isRoadmap = protocol.status === 'roadmap'

	const chipCls = cn(
		'flex items-center gap-3 rounded-full border py-2 pr-4 pl-2',
		isLive && 'border-border bg-card',
		isWip && 'border-border bg-card opacity-70 grayscale',
		isRoadmap && 'border-dashed border-border/60 bg-transparent',
	)
	const labelCls = cn(
		'text-sm font-medium leading-none',
		isRoadmap && 'text-muted-foreground',
	)
	const iconWrapTint = isRoadmap
		? 'bg-transparent border border-dashed border-border/60 text-muted-foreground'
		: cn('text-white', protocol.tint)

	return (
		<div
			className={chipCls}
			title={isWip ? 'In progress' : isRoadmap ? 'Not decoded yet - becomes margin of error' : undefined}
		>
			{protocol.simpleIcon ? (
				<div
					className={cn(
						'flex size-8 items-center justify-center rounded-full',
						isRoadmap
							? 'bg-transparent border border-dashed border-border/60'
							: 'bg-secondary',
					)}
				>
					<img
						src={`/logos/${protocol.simpleIcon}.svg`}
						alt=""
						aria-hidden="true"
						className={cn('size-4', isRoadmap && 'opacity-50')}
					/>
				</div>
			) : (
				<div
					className={cn(
						'flex size-8 items-center justify-center rounded-full text-xs font-semibold',
						iconWrapTint,
					)}
				>
					{initials}
				</div>
			)}
			<div>
				<div className={labelCls}>{protocol.label}</div>
				<div className="text-muted-foreground text-[10px] leading-none">
					{protocol.category}
				</div>
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// 3. What you get (Reports) — moved here from /more on 2026-08-18 so the
// homepage carries the deliverable signal without a click.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// FAQ — answers the questions a first-touch visitor lands on the homepage
// carrying: what's the wedge vs Koinly/CTC, coverage honesty, custody,
// public-vs-paid split, tax-vs-compute vocabulary. Copy is deliberately
// terse and honest — no marketing hedges, no "audit" vocab (memory
// `feedback-audit-is-wrong-vocabulary`), per-protocol coverage caveats
// visible (memory `feedback-per-protocol-not-per-wallet`).
// ---------------------------------------------------------------------------

const FAQ_ITEMS: Array<{ q: string; a: React.ReactNode }> = [
	{
		q: 'Why do I have to sign up to use the product?',
		a: (
			<>
				Reading a wallet means on-chain RPC calls, protocol decoding, and
				price lookups — paid API credits per wallet, not free. Signing
				up (free) lets me rate-limit per account and keep the service
				sustainable without burning credits on bots. Browse the X
				account for live report examples before deciding.
			</>
		),
	},
	{
		q: 'My chain or protocol isn’t listed. Do you offer a service?',
		a: (
			<>
				I’m actively adding wallets, chains and protocols to the automated
				product. If your situation needs coverage I don’t have yet —
				different chains, unsupported protocols, or manual entries from
				screenshots, recordings, or CSV files — contact me for a
				personalised service.
			</>
		),
	},
	{
		q: 'My wallet is in the public listing. Can I remove it?',
		a: <>Yes. DM me on X (see below) and I’ll remove it, same day.</>,
	},
]

// ---------------------------------------------------------------------------
// Purchase tax report — pricing section. Tiered by (wallets, tx count).
// USD default matches the X-audience base-currency convention
// (memory `feedback-x-audience-base-currency-usd`); AUD is the operator's
// billing context, not the marketing one.
//
// Anchor pricing off Koinly + a DeFi premium — Koinly Pro is ~$279/yr for
// 10k tx of hodling; we charge ~1.5x their equivalent tier for the DeFi
// surface (LP position P&L, per-position fees, wallet rollup) because
// the alternative today is screenshotting positions into Koinly's manual-
// entry tool. Placeholder prices below — edit in place.
// ---------------------------------------------------------------------------

interface PricingTier {
	name: string
	price: string
	cadence: string
	strapline: string
	includedLine: string
	limits: string[]
	recommended?: boolean
}

const PRICING_TIERS: PricingTier[] = [
	{
		name: 'Starter',
		price: '$99',
		cadence: 'per financial year',
		strapline: 'One wallet, light DeFi activity.',
		includedLine: '1 wallet',
		limits: ['Up to 500 DeFi transactions', 'One financial year', 'All supported protocols'],
	},
	{
		name: 'Trader',
		price: '$249',
		cadence: 'per financial year',
		strapline: 'Multiple wallets, heavy LP activity.',
		includedLine: '5 wallets',
		limits: ['Up to 5,000 DeFi transactions', 'One financial year', 'All supported protocols'],
		recommended: true,
	},
	{
		name: 'Power',
		price: '$499',
		cadence: 'per financial year',
		strapline: 'Every wallet you actively LP from.',
		includedLine: '20 wallets',
		limits: ['Up to 20,000 DeFi transactions', 'One financial year', 'All supported protocols'],
	},
]

const PRICING_INCLUDED_ALL_TIERS = [
	'DeFi transactions decoded from on-chain events',
	'FIFO cost basis, per lot',
	'Wallet-level and per-position P&L',
	'CSV + PDF export for your accountant',
	'Up to 1 hour of expert review with the founder',
]

function Pricing() {
	return (
		<SiteSection wide id="pricing">
			<p className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
				Pricing
			</p>
			<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
				Purchase a tax report.
			</h2>
			<p className="text-muted-foreground mt-3 max-w-2xl text-base md:text-lg">
				One report per financial year, priced by transaction count and wallet
				count. Every tier decodes DeFi from on-chain events; the tiers only
				differ on volume.
			</p>

			<div className="mt-10 flex flex-col gap-4">
				{PRICING_TIERS.map((tier) => (
					<div
						key={tier.name}
						className={cn(
							'flex flex-col gap-6 rounded-xl border p-6 md:flex-row md:items-center',
							tier.recommended
								? 'border-foreground/30 bg-card/60'
								: 'border-border/60 bg-card/30',
						)}
					>
						{/* Tier identity — name + strapline. Left column on md+. */}
						<div className="md:w-56 md:shrink-0">
							<div className="flex items-baseline gap-2">
								<h3 className="text-lg font-semibold tracking-tight">{tier.name}</h3>
								{tier.recommended ? (
									<span className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
										Recommended
									</span>
								) : null}
							</div>
							<p className="text-muted-foreground mt-1 text-sm">{tier.strapline}</p>
						</div>

						{/* Price. Middle column on md+. */}
						<div className="md:w-48 md:shrink-0">
							<div className="flex items-baseline gap-2">
								<span className="text-3xl font-bold tracking-tight tabular-nums">
									{tier.price}
								</span>
								<span className="text-muted-foreground text-xs">
									USD · {tier.cadence}
								</span>
							</div>
						</div>

						{/* Limits — expands. */}
						<ul className="flex-1 space-y-1 text-sm">
							<li className="text-foreground font-medium">{tier.includedLine}</li>
							{tier.limits.map((limit) => (
								<li key={limit} className="text-muted-foreground">
									{limit}
								</li>
							))}
						</ul>

						{/* CTA — right column on md+. Self-serve purchase not
						    wired yet; toast tells the visitor and points at
						    #contact for the invitation path. */}
						<button
							type="button"
							onClick={() =>
								toast('Coming soon', {
									description: 'Self-serve features are on the way. Get in touch on x to arrange access in the meantime.',
								})
							}
							className={cn(
								buttonVariants({ size: 'sm', variant: tier.recommended ? 'default' : 'outline' }),
								'md:w-32 md:shrink-0',
							)}
						>
							Buy →
						</button>
					</div>
				))}
			</div>

			<div className="mt-10 rounded-xl border border-border/40 bg-card/20 p-6">
				<h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
					Every tier includes
				</h3>
				<ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
					{PRICING_INCLUDED_ALL_TIERS.map((item) => (
						<li key={item} className="text-foreground/90">
							— {item}
						</li>
					))}
				</ul>
			</div>

			<p className="text-muted-foreground mt-6 text-sm">
				Higher volume, multiple financial years, or an unsupported chain?
				See <a href="#contact" className="text-foreground hover:underline">Contact</a> —
				personalised work is charged separately.
			</p>
		</SiteSection>
	)
}

function FAQ() {
	return (
		<SiteSection wide id="faq">
			<p className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
				FAQ
			</p>
			<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
				Before you ask.
			</h2>
			<Accordion type="single" collapsible className="mt-8 w-full">
				{FAQ_ITEMS.map((item, i) => (
					<AccordionItem key={i} value={`faq-${i}`}>
						<AccordionTrigger className="text-left text-base font-medium md:text-lg">
							{item.q}
						</AccordionTrigger>
						<AccordionContent className="text-muted-foreground text-base leading-relaxed md:text-lg">
							{item.a}
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</SiteSection>
	)
}

// Contact — renamed from "About" 2026-09-12 (FAQ absorbed the "About"
// eyebrow label; this block is now the pitch + CTA). Deliberately keeps
// the SCOPE BOUNDARY line (I don't file / take custody / hold keys)
// inline in the bio prose — that's constraint #1 in the header
// docstring and needs to stay visible on the page, not just documented
// in code.
function Contact() {
	return (
		<SiteSection wide id="contact">
			<p className="text-muted-foreground mb-3 text-sm font-medium tracking-wide uppercase">
				Contact
			</p>
			<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
				Get in touch.
			</h2>
			<p className="text-muted-foreground mt-6 max-w-2xl text-lg">
				No custody, no keys, no filing, not financial advice.
			</p>
			<div className="mt-6 flex flex-col gap-3 sm:flex-row">
				<a
					href="https://x.com/underemployed"
					rel="noopener noreferrer me"
					target="_blank"
					className={cn(buttonVariants({ size: 'lg' }))}
				>
					DM @underemployed on X →
				</a>
				<a
					href="https://cal.com/jacobscalendar/30min"
					rel="noopener noreferrer"
					target="_blank"
					className={cn(buttonVariants({ size: 'lg', variant: 'outline' }))}
				>
					Schedule demo
				</a>
			</div>
		</SiteSection>
	)
}

